


































































































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import * as routes from "@/employer/route-names";
import { FundingOption, UserRole, Permission, FeatureFlags } from "@/shared/types";
import Swinger from "@/employer/components/shared/Swinger.vue";
import { isPartnerPage } from "@/employer/navSelector";
import { useUserStore } from "@/shared/store/user";
import { useNavigationStore } from "@/shared/store/navigation";
import { usePortalCustomizationStore } from "@/shared/store/portalCustomization";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";

@Component({
  components: {
    Swinger
  }
})
export default class MainNavigation extends Vue {
  userStore = useUserStore();
  navigationStore = useNavigationStore();
  portalCustomizationStore = usePortalCustomizationStore();
  rolePermissionsStore = useRolePermissionsStore();
  featureFlagStore = useFeatureFlagStore();
  custodianSettingsStore = useCustodianSettingsStore();


  @Prop()
  partnerHeaderMode: {
    showCustomerServiceHeader: boolean;
    showEmployerNameHeader: boolean;
  };

  routes = routes;

  get navigationRouteName () {
    return this.$route.name;
  }

  get isPartnerAccountHolderInvestmentRoute () {
    return this.routes.PartnerAccountHolderInvestmentRoutes.includes(this.navigationRouteName);
  }

  get displayPartnerGroupsLink (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.PartnerEmployerRead);
  }

  get displayPartnerAccountHoldersLink (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.PartnerFindEmployeeRead);
  }

  get displayEmployeesLink (): boolean {
    return this.rolePermissionsStore.userHasPermission(Permission.EmployeeRead);
  }

  get displayContributionsLink (): boolean {
    if (!this.rolePermissionsStore.userHasPermission(Permission.PayrollRead)) {
      return false;
    }

    return this.userStore.currentEmployer
      && (this.userStore.currentEmployer.fundingOption !== FundingOption.ErCalculateErPush
        || (this.showFundingMethod && this.userStore.currentEmployer.hasFundingOptionHistory));
  }

  get defaultToManageFundingFileTab (): boolean {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.fundingOption === FundingOption.ErCalculateBendPull;
  }

  get defaultToHistoryTab (): boolean {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.fundingOption === FundingOption.ErCalculateErPush;
  }

  get isSettingsRoute (): boolean {
    return this.routes.SettingsRoutes.includes(this.navigationRouteName);
  }

  get shouldShowEmployerReportsLink () {
    return this.userStore.currentEmployer && this.userStore.currentEmployer.employerUserRoles &&
      this.userStore.currentEmployer.employerUserRoles.includes(UserRole.EmployeeManager);
  }

  get showFundingMethod () {
    return this.featureFlagStore.flags.showFundingMethod;
  }

  get showResourceForms () {
    return this.featureFlagStore.flags.bend16362ResourceFormsInEmployer &&
      this.rolePermissionsStore.userHasPartnerPermission(Permission.PartnerManageFormDocuments, this.userStore.partnerId);
  }

  get isEmployeesRoute (): boolean {
    return this.routes.EmployeesRoutes.includes(this.navigationRouteName);
  }

  get isContributionsRoute (): boolean {
    return this.routes.ContributionsRoutes.includes(this.navigationRouteName);
  }

  get displayInitials () {
    if (!this.userStore.currentUser.firstName || !this.userStore.currentUser.lastName) {
      // Whatever the default initials should be if you don't have a name
      return "NA";
    }

    return `${this.userStore.currentUser.firstName[0]}${this.userStore.currentUser.lastName[0]}`;
  }

  get currentUserEmail () {
    if (!this.userStore.currentUser.email) {
      return "guest";
    }

    return this.userStore.currentUser.email;
  }

  get usePartnerNavigation () {
    return isPartnerPage(this.$route.name);
  }

  get useServiceNavigation () {
    return this.partnerHeaderMode && this.partnerHeaderMode.showCustomerServiceHeader;
  }

  get partnerHeaderShow () {
    return this.partnerHeaderMode &&
      (this.partnerHeaderMode.showEmployerNameHeader || this.partnerHeaderMode.showCustomerServiceHeader);
  }

  get useEmployerNavigation () {
    return this.partnerHeaderMode.showEmployerNameHeader
      || !(this.useServiceNavigation || this.usePartnerNavigation);
  }

  get contributionsLinkTo () {
    if (this.defaultToManageFundingFileTab) {
      return { name: routes.ManagePayrollFundingFile, params: { page: 1 } };
    } else if (this.defaultToHistoryTab) {
      return { name: routes.ContributionsHistory };
    } else {
      return { name: routes.Contributions };
    }
  }

  get displayAccountManagement () {
    return this.custodianSettingsStore.displayBankAccountHolds;
  }

  get shouldShowPartnerReportsLink () {
    // currently, only available for Partner Admins
    return this.userStore.currentPartner && this.userStore.currentPartner.partnerUserRoles &&
      this.userStore.currentPartner.partnerUserRoles.includes(UserRole.Admin);
  }

  created (): void {
    this.$router.afterEach(this.closeNav);
  }

  closeNav () {
    this.navigationStore.closeMobileMenu();
  }

  routeToPartnerSecurity () {
    return { name: routes.PartnerSecurity, params: { partnerId: this.userStore.partnerId } };
  }
}
